/**
 * React Query mutation hooks for tap assignment management.
 *
 * All mutations invalidate the ['taps', locationId] query on success so the
 * tap list re-fetches automatically.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  kickTapAssignment,
  promoteTapAssignment,
  createTapAssignment,
  patchTapAssignment,
} from '../api/client';
import type { CreateAssignmentBody, PatchAssignmentBody } from '../api/client';

/** Kick the current 'on' assignment. Pass promoteReserved=true to atomically
 *  promote the queued 'reserved' assignment to 'on'. */
export function useKickTap(locationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      promoteReserved,
    }: {
      assignmentId: string;
      promoteReserved: boolean;
    }) => kickTapAssignment(assignmentId, promoteReserved),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taps', locationId] });
    },
  });
}

/** Promote a 'reserved' assignment to 'on' (without kicking). */
export function usePromoteTap(locationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => promoteTapAssignment(assignmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taps', locationId] });
    },
  });
}

/** Create a new tap assignment ('on' or 'reserved'). */
export function useAssignBeer(locationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tapId, body }: { tapId: string; body: CreateAssignmentBody }) =>
      createTapAssignment(tapId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taps', locationId] });
    },
  });
}

/** Edit beer, keg size, or notes on an existing active assignment. */
export function useEditAssignment(locationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      body,
    }: {
      assignmentId: string;
      body: PatchAssignmentBody;
    }) => patchTapAssignment(assignmentId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taps', locationId] });
    },
  });
}
